import type {
  BetLookupInput,
  BetLookupOptions,
  BetlensResult,
  BusinessClientSummary,
  GameSummary,
} from '@/types';
import type { LookupParams } from '@/lib/bootstrap';

/**
 * The iframe PULLS the bundle from the panel's mounted betlens API.
 * One POST returns everything — data + hints together.
 */

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export function toInput(lookup: LookupParams): BetLookupInput {
  const psid = lookup.play_service_id;
  if (lookup.round_id && lookup.game_uuid) {
    return { play_service_id: psid, game_uuid: lookup.game_uuid, round_id: lookup.round_id };
  }
  if (lookup.bet_id && lookup.game_uuid) {
    return { play_service_id: psid, game_uuid: lookup.game_uuid, bet_id: lookup.bet_id };
  }
  if (lookup.transaction_id) {
    return { play_service_id: psid, transaction_id: lookup.transaction_id };
  }
  if (lookup.cycle_uniq_id) {
    return { play_service_id: psid, cycle_uniq_id: lookup.cycle_uniq_id };
  }
  if (lookup.tr_uniq_id) {
    return { play_service_id: psid, tr_uniq_id: lookup.tr_uniq_id };
  }
  throw new Error(
    'Provide game_uuid + bet_id, a transaction id, a cycle_uniq_id, or a tr_uniq_id.',
  );
}

/** The selectable projects for the lookup dropdown. */
export async function fetchClients(params: {
  api: string;
  token: string | null;
  signal?: AbortSignal;
}): Promise<BusinessClientSummary[]> {
  const base = params.api.replace(/\/+$/, '');
  const res = await fetch(`${base}/clients`, {
    headers: params.token ? { authorization: `Bearer ${params.token}` } : {},
    signal: params.signal,
  }).catch(() => {
    throw new ApiError(`Could not load projects from ${base}.`, 0);
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new ApiError(body?.error ?? `Could not load projects (${res.status})`, res.status);
  }
  const body = (await res.json()) as { clients?: BusinessClientSummary[] };
  return body.clients ?? [];
}

/** The games available to a project — for the "pick a game by name" selector. */
export async function fetchGames(params: {
  api: string;
  token: string | null;
  playServiceId: string;
  signal?: AbortSignal;
}): Promise<GameSummary[]> {
  const base = params.api.replace(/\/+$/, '');
  const res = await fetch(
    `${base}/games?play_service_id=${encodeURIComponent(params.playServiceId)}`,
    {
      headers: params.token ? { authorization: `Bearer ${params.token}` } : {},
      signal: params.signal,
    },
  ).catch(() => {
    throw new ApiError(`Could not load games from ${base}.`, 0);
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new ApiError(body?.error ?? `Could not load games (${res.status})`, res.status);
  }
  const body = (await res.json()) as { games?: GameSummary[] };
  return body.games ?? [];
}

export async function fetchBet(params: {
  api: string;
  token: string | null;
  input: BetLookupInput;
  options?: BetLookupOptions;
  signal?: AbortSignal;
}): Promise<BetlensResult> {
  const base = params.api.replace(/\/+$/, '');

  let res: Response;
  try {
    res = await fetch(`${base}/bet`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        ...(params.token ? { authorization: `Bearer ${params.token}` } : {}),
      },
      body: JSON.stringify({ input: params.input, options: params.options }),
      signal: params.signal,
    });
  } catch (err) {
    // Network/CORS failures surface as an opaque TypeError — say something useful.
    if ((err as Error)?.name === 'AbortError') throw err;
    throw new ApiError(
      `Could not reach the betlens API at ${base}. Check the API base URL, that the ` +
        `backend is running, and that it allows this origin (CORS).`,
      0,
    );
  }

  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new ApiError(body?.error ?? `Request failed (${res.status})`, res.status);
  }

  return (await res.json()) as BetlensResult;
}
